import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BugReportIcon from '@mui/icons-material/BugReport';
import PersonIcon from '@mui/icons-material/Person';
import ComputerIcon from '@mui/icons-material/Computer';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { AdminBugReport } from '../../../types/admin';
import { format } from 'date-fns';

interface BugReportDetailModalProps {
  open: boolean;
  report: AdminBugReport | null;
  onClose: () => void;
}

const getSeverityColor = (severity: string): 'error' | 'warning' | 'info' | 'default' => {
  switch (severity) {
    case 'critical':
      return 'error';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    case 'low':
    default:
      return 'default';
  }
};

/**
 * BugReportDetailModal - Modal displaying full bug report details
 *
 * Features:
 * - Complete bug report information
 * - Reporter context (browser, OS, URL)
 * - Status and severity indicators
 * - Assignment information
 */
export default function BugReportDetailModal({
  open,
  report,
  onClose,
}: BugReportDetailModalProps) {
  if (!report) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BugReportIcon />
            Bug Report #{report.id.split('_')[1]}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={report.severity.toUpperCase()}
              color={getSeverityColor(report.severity)}
              size="small"
            />
            <Chip
              label={report.status.replace('_', ' ').toUpperCase()}
              color={
                report.status === 'resolved'
                  ? 'success'
                  : report.status === 'in_progress'
                  ? 'warning'
                  : report.status === 'closed'
                  ? 'default'
                  : 'info'
              }
              size="small"
            />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Subject and Description */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {report.subject}
            </Typography>
            <Chip
              label={report.category}
              size="small"
              variant="outlined"
              sx={{ mb: 2 }}
            />
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {report.description}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Reporter Information */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              Reporter Information
            </Typography>
            <List dense>
              <ListItem>
                <PersonIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
                <ListItemText
                  primary={report.user_name || 'Unknown'}
                  secondary={report.user_email}
                />
              </ListItem>
              <ListItem>
                <CalendarMonthIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
                <ListItemText
                  primary="Reported"
                  secondary={format(new Date(report.created_at), 'PPP p')}
                />
              </ListItem>
              {report.updated_at && (
                <ListItem>
                  <CalendarMonthIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
                  <ListItemText
                    primary="Last Updated"
                    secondary={format(new Date(report.updated_at), 'PPP p')}
                  />
                </ListItem>
              )}
            </List>
          </Grid>

          {/* Reporter Context */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              Reporter Context
            </Typography>
            {report.reporter_context && (
              <List dense>
                <ListItem>
                  <ComputerIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
                  <ListItemText
                    primary="Browser"
                    secondary={report.reporter_context.browser}
                  />
                </ListItem>
                <ListItem>
                  <ComputerIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
                  <ListItemText
                    primary="Operating System"
                    secondary={report.reporter_context.os}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Page URL"
                    secondary={report.reporter_context.url}
                  />
                </ListItem>
              </List>
            )}
          </Grid>

          {report.assigned_to && (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Assignment
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Assigned to: <strong>{report.assigned_to}</strong>
                </Typography>
              </Grid>
            </>
          )}

          {report.attachments && report.attachments.length > 0 && (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Attachments ({report.attachments.length})
                </Typography>
                <List dense>
                  {report.attachments.map((attachment, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={attachment} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
